/*
 * useEdgeFeature — does the selected edge's build serve this route?
 *
 * WHY THIS EXISTS
 *   The console rolls forward on its own cadence; appliances roll forward one
 *   at a time, over weeks, at whatever hour a site will accept a restart. So a
 *   panel written today is, for a while, a panel most of the fleet cannot
 *   answer. The wrong behaviours are a spinner that never resolves, an empty
 *   chart that reads as "zero", and a red error that reads as "broken". The
 *   right one is a sentence saying this box has not been updated yet.
 *
 *   WanNames solved this once inline with a `supported` flag. This is the same
 *   idea lifted out, so the next panel gets it for free instead of inventing a
 *   third variation.
 *
 * WHAT COUNTS AS ABSENT
 *   404 and 501 only. A timeout or a 5xx means the edge is having a bad day,
 *   not that it lacks the feature, and telling an operator a feature does not
 *   exist because a proxy hop timed out would send them to rebuild a box that
 *   is already fine. Those stay "unknown" and retry.
 *
 * ! file hooks/useEdgeFeature.js
 * ---------------
 * \author: Tolga O. Atalay
 * \Affiliation: A2 Labs LLC / A12XT Inc.
 * \date: 2026
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { nodeApiUrlFor } from "../serverApi";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";

export const FEATURE_UNKNOWN = "unknown";
export const FEATURE_PRESENT = "present";
export const FEATURE_ABSENT = "absent";
export const FEATURE_UNREACHABLE = "unreachable";

/**
 * @param {string} nodeId   the selected edge
 * @param {string} path     the route to probe, e.g. "/power"
 * @returns {{state: string, payload: any, refresh: () => void}}
 *   payload is the parsed body when the route answered, so a caller that
 *   probes and reads in one step does not fetch twice.
 */
export default function useEdgeFeature(nodeId, path) {
  const [state, setState] = useState(FEATURE_UNKNOWN);
  const [payload, setPayload] = useState(null);
  const seq = useRef(0);

  const probe = useCallback(async () => {
    if (!nodeId) {
      setState(FEATURE_UNKNOWN);
      setPayload(null);
      return;
    }
    const mine = ++seq.current;
    const url = nodeApiUrlFor(nodeId)(path);
    if (!url) return;
    const res = await fetchWithTimeout(url).catch(() => null);
    if (mine !== seq.current) return; // a newer probe (or node) superseded this
    if (!res) {
      setState(FEATURE_UNREACHABLE);
      return;
    }
    if (res.status === 404 || res.status === 501) {
      setState(FEATURE_ABSENT);
      setPayload(null);
      return;
    }
    if (!res.ok) {
      setState(FEATURE_UNREACHABLE);
      return;
    }
    const body = await res.json().catch(() => null);
    if (mine !== seq.current) return;
    setState(FEATURE_PRESENT);
    setPayload(body);
  }, [nodeId, path]);

  useEffect(() => {
    // Reset before probing: the previous edge's verdict must not describe this
    // one, the same way its data must not.
    setState(FEATURE_UNKNOWN);
    setPayload(null);
    probe();
  }, [probe]);

  return { state, payload, refresh: probe };
}
